const API_URL = process.env.REACT_APP_API_URL || '';
const ENDPOINT = `${API_URL}/api/funnel/event`;
const FUNNEL_TIMEOUT_MS = 3000;

const ANON_ID_KEY = 'jjj_funnel_anon_id';
const SESSION_ID_KEY = 'jjj_funnel_session_id';
const JOURNEY_ID_KEY = 'jjj_funnel_journey_id';
const PENDING_SIGN_IN_KEY = 'jjj_pending_sign_in_intent';

function createId(prefix) {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return `${prefix}_${crypto.randomUUID()}`;
  }
  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

function getPersistentId(storage, key, prefix) {
  try {
    const existing = storage.getItem(key);
    if (existing) return existing;
    const created = createId(prefix);
    storage.setItem(key, created);
    return created;
  } catch (e) {
    return createId(prefix);
  }
}

export function getFunnelIdentity() {
  return {
    anonId: getPersistentId(window.localStorage, ANON_ID_KEY, 'anon'),
    sessionId: getPersistentId(window.sessionStorage, SESSION_ID_KEY, 'sess'),
    journeyId: getPersistentId(window.sessionStorage, JOURNEY_ID_KEY, 'journey'),
  };
}

function getAttribution() {
  const params = new URLSearchParams(window.location.search);
  return {
    utm_source: params.get('utm_source'),
    utm_medium: params.get('utm_medium'),
    utm_campaign: params.get('utm_campaign'),
    utm_term: params.get('utm_term'),
    utm_content: params.get('utm_content'),
    fbclid: params.get('fbclid'),
  };
}

function normalizeTrackedPath(inputPath) {
  try {
    const rawPath = inputPath || `${window.location.pathname}${window.location.search}`;
    const url = new URL(rawPath, window.location.origin);
    const params = new URLSearchParams(url.search);

    for (const key of Array.from(params.keys())) {
      if (
        key.startsWith('utm_') ||
        key === 'fbclid' ||
        key === 'gclid' ||
        key === 'msclkid'
      ) {
        params.delete(key);
      }
    }

    const cleanedSearch = params.toString();
    return cleanedSearch ? `${url.pathname}?${cleanedSearch}` : url.pathname;
  } catch (e) {
    return inputPath || window.location.pathname;
  }
}

function buildPayload(event, properties = {}, options = {}) {
  const { anonId, sessionId, journeyId } = getFunnelIdentity();
  return {
    event,
    journey_id: journeyId,
    session_id: sessionId,
    anon_id: anonId,
    clerk_user_id: options.clerkUserId || null,
    path: normalizeTrackedPath(options.path),
    referrer: options.referrer ?? (document.referrer || null),
    properties,
    ...getAttribution(),
  };
}

export async function trackFunnelEvent(event, properties = {}, options = {}) {
  const payload = buildPayload(event, properties, options);
  const body = JSON.stringify(payload);

  if (options.useBeacon && navigator.sendBeacon) {
    const blob = new Blob([body], { type: 'application/json' });
    navigator.sendBeacon(ENDPOINT, blob);
    return;
  }

  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), FUNNEL_TIMEOUT_MS);
  try {
    await fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: Boolean(options.keepalive),
      credentials: 'same-origin',
      signal: controller.signal,
    });
  } catch (e) {
    // Tracking must never break user flows.
  } finally {
    window.clearTimeout(timeoutId);
  }
}

export function rememberSignInIntent(source, properties = {}) {
  try {
    window.sessionStorage.setItem(
      PENDING_SIGN_IN_KEY,
      JSON.stringify({
        source,
        properties,
        created_at: Date.now(),
      })
    );
  } catch (e) {
    // Ignore storage failures; sign-in should still work.
  }
}

export function consumeSignInIntent() {
  try {
    const raw = window.sessionStorage.getItem(PENDING_SIGN_IN_KEY);
    if (!raw) return null;
    window.sessionStorage.removeItem(PENDING_SIGN_IN_KEY);
    return JSON.parse(raw);
  } catch (e) {
    return null;
  }
}
